import { useState, FormEvent, useEffect } from 'react';
import { api } from '../lib/api';

interface Ticket {
  id: string;
  subject: string;
  status: 'open' | 'in_progress' | 'waiting_on_customer' | 'resolved' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  created_at: string;
  updated_at: string;
  last_reply?: string;
}

interface KBArticle {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  url?: string;
}

type TabId = 'submit' | 'tickets' | 'kb';

const TABS: { id: TabId; label: string }[] = [
  { id: 'submit', label: 'Submit Ticket' },
  { id: 'tickets', label: 'My Tickets' },
  { id: 'kb', label: 'Knowledge Base' },
];

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  waiting_on_customer: 'bg-orange-100 text-orange-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-500',
};

const PRIORITY_COLORS: Record<string, string> = {
  urgent: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  normal: 'bg-blue-100 text-blue-700',
  low: 'bg-gray-100 text-gray-500',
};

// ---- Submit Ticket Tab ----
function SubmitTicketTab() {
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState('normal');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ticketId, setTicketId] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await api.post<{ ticket_id: string }>('/support/tickets', {
        subject, category, priority, message,
      });
      setTicketId(result.ticket_id);
      setSuccess(true);
      setSubject('');
      setCategory('');
      setPriority('normal');
      setMessage('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit ticket');
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <div className="text-center py-8">
        <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Ticket Submitted</h3>
        {ticketId && <p className="text-sm text-gray-500 mb-1">Ticket ID: <span className="font-mono font-medium">{ticketId}</span></p>}
        <p className="text-sm text-gray-500 mb-6">We'll get back to you within 24 hours via email.</p>
        <button
          onClick={() => setSuccess(false)}
          className="bg-indigo-600 text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-indigo-700"
        >
          Submit Another Ticket
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 max-w-2xl">
      {error && <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">{error}</div>}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
        <input
          type="text" required value={subject} onChange={e => setSubject(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="Brief description of your issue"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select
            value={category} onChange={e => setCategory(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">Select a category</option>
            <option value="billing">Billing & Payments</option>
            <option value="scanning">Scanning & Findings</option>
            <option value="integrations">GitHub / Integrations</option>
            <option value="account">Account & Access</option>
            <option value="pentest">AI Pentests</option>
            <option value="reports">Reports</option>
            <option value="bug">Bug Report</option>
            <option value="feature">Feature Request</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
          <select
            value={priority} onChange={e => setPriority(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="low">Low — general question</option>
            <option value="normal">Normal — needs help soon</option>
            <option value="high">High — blocking work</option>
            <option value="urgent">Urgent — security incident</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
        <textarea
          required value={message} onChange={e => setMessage(e.target.value)} rows={6}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="Describe your issue in detail. Include any error messages, steps to reproduce, and what you expected to happen."
        />
      </div>

      <button
        type="submit" disabled={submitting}
        className="bg-indigo-600 text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
      >
        {submitting ? (
          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : null}
        {submitting ? 'Submitting...' : 'Submit Ticket'}
      </button>
    </form>
  );
}

// ---- My Tickets Tab ----
function MyTicketsTab() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ tickets: Ticket[] }>('/support/tickets')
      .then(data => setTickets(data.tickets ?? []))
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load tickets'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-gray-100 rounded-lg" />)}
      </div>
    );
  }

  if (error) return <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">{error}</div>;

  if (tickets.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400 text-sm">
        <svg className="w-10 h-10 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        No support tickets yet. Submit your first ticket if you need help.
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
      {tickets.map(ticket => (
        <div key={ticket.id} className="px-5 py-4 hover:bg-gray-50">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-sm font-medium text-gray-900 truncate">{ticket.subject}</p>
              </div>
              <div className="flex flex-wrap gap-2 text-xs text-gray-400">
                <span className="font-mono">{ticket.id}</span>
                <span>·</span>
                <span>Created {new Date(ticket.created_at).toLocaleDateString()}</span>
                <span>·</span>
                <span>Updated {new Date(ticket.updated_at).toLocaleDateString()}</span>
              </div>
              {ticket.last_reply && (
                <p className="text-xs text-gray-500 mt-1 truncate">Last: {ticket.last_reply}</p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${PRIORITY_COLORS[ticket.priority] ?? 'bg-gray-100 text-gray-600'}`}>
                {ticket.priority}
              </span>
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[ticket.status] ?? 'bg-gray-100 text-gray-600'}`}>
                {ticket.status.replace(/_/g, ' ')}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- Knowledge Base Tab ----
function KnowledgeBaseTab() {
  const [query, setQuery] = useState('');
  const [articles, setArticles] = useState<KBArticle[]>([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load featured articles on mount
  useEffect(() => {
    api.get<{ articles: KBArticle[] }>('/support/kb')
      .then(data => setArticles(data.articles ?? []))
      .catch(() => {});
  }, []);

  async function handleSearch(e: FormEvent) {
    e.preventDefault();
    if (!query.trim()) return;
    setError(null);
    setSearching(true);
    try {
      const data = await api.get<{ articles: KBArticle[] }>(`/support/kb?q=${encodeURIComponent(query)}`);
      setArticles(data.articles ?? []);
      setSearched(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setSearching(false);
    }
  }

  const categories = [...new Set(articles.map(a => a.category))].filter(Boolean);

  return (
    <div className="space-y-5 max-w-3xl">
      <form onSubmit={handleSearch} className="flex gap-3">
        <input
          type="text" value={query} onChange={e => setQuery(e.target.value)}
          className="flex-1 border border-gray-300 rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="Search knowledge base... (e.g. how to add a platform, CVSS score)"
        />
        <button
          type="submit" disabled={searching}
          className="bg-indigo-600 text-white px-5 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
        >
          {searching ? (
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          )}
          Search
        </button>
      </form>

      {error && <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">{error}</div>}

      {searched && articles.length === 0 && (
        <div className="text-center py-8 text-gray-400 text-sm">
          No articles found for "{query}". Try different keywords or submit a support ticket.
        </div>
      )}

      {articles.length > 0 && (
        categories.length > 1 ? (
          categories.map(cat => {
            const catArticles = articles.filter(a => a.category === cat);
            return (
              <div key={cat}>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{cat}</h3>
                <div className="space-y-2">
                  {catArticles.map(article => (
                    <a
                      key={article.id}
                      href={article.url ?? '#'}
                      target={article.url ? '_blank' : undefined}
                      rel="noopener noreferrer"
                      className="block border border-gray-200 rounded-lg p-4 hover:bg-gray-50 hover:border-indigo-200 transition-colors"
                    >
                      <p className="text-sm font-medium text-indigo-700 hover:underline">{article.title}</p>
                      <p className="text-xs text-gray-500 mt-1">{article.excerpt}</p>
                    </a>
                  ))}
                </div>
              </div>
            );
          })
        ) : (
          <div className="space-y-2">
            {articles.map(article => (
              <a
                key={article.id}
                href={article.url ?? '#'}
                target={article.url ? '_blank' : undefined}
                rel="noopener noreferrer"
                className="block border border-gray-200 rounded-lg p-4 hover:bg-gray-50 hover:border-indigo-200 transition-colors"
              >
                {article.category && (
                  <span className="text-xs text-gray-400 uppercase tracking-wide">{article.category}</span>
                )}
                <p className="text-sm font-medium text-indigo-700 hover:underline mt-0.5">{article.title}</p>
                <p className="text-xs text-gray-500 mt-1">{article.excerpt}</p>
              </a>
            ))}
          </div>
        )
      )}

      {/* Contact fallback */}
      <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-5 flex items-start gap-4">
        <svg className="w-6 h-6 text-indigo-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        <div>
          <p className="text-sm font-medium text-indigo-900">Can't find what you need?</p>
          <p className="text-sm text-indigo-700 mt-0.5">
            Our support team is available Monday–Friday, 9am–6pm EST.{' '}
            <a href="mailto:support@theonesecurity.app" className="font-medium underline hover:no-underline">
              Email us directly
            </a>{' '}
            or submit a ticket from the "Submit Ticket" tab.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---- Main ----
export default function SupportPage() {
  const [activeTab, setActiveTab] = useState<TabId>('submit');

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Support</h1>

      <div className="bg-white rounded-lg shadow">
        {/* Tab nav */}
        <div className="border-b border-gray-200 px-6">
          <nav className="flex gap-6">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'submit' && <SubmitTicketTab />}
          {activeTab === 'tickets' && <MyTicketsTab />}
          {activeTab === 'kb' && <KnowledgeBaseTab />}
        </div>
      </div>
    </div>
  );
}
